import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { addDoc, collection, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import {
  numeroCbuq,
} from "../CadastroTracoCbuq/tracoCbuqCalculos";
import { criarGranulometriaAsfalto } from "./faixasDnitAsfaltoModel";

export const COLECAO_CARACTERIZACAO_ASFALTO = "tblCaracterizacaoAgregadosAsfalto";

const tres = () => Array.from({ length: 3 }, () => ({}));
const inicial = {
  codigo: "", data: new Date().toISOString().slice(0, 10), tipoAgregado: "",
  materialId: "", material: "", codigoMaterial: "", cliente: "", clienteId: "", obraId: "", obra: "",
  procedencia: "", camadaEstudo: "", observacoes: "", temperaturaAgua: "25", determinacoes: tres(),
};

function formatar(valor, casas = 3) {
  const numero = numeroCbuq(valor);
  return numero === null ? "-" : numero.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function textoBusca(valor) {
  return String(valor || "").trim().toLowerCase();
}

function tipoAgregadoDoMaterial(material) {
  if (material?.IsAgregadoGraudo && !material?.IsAgregadoMiudo) return "graudo";
  if (material?.IsAgregadoMiudo && !material?.IsAgregadoGraudo) return "miudo";
  return "";
}

export default function CadastroCaracterizacaoAsfalto() {
  const history = useHistory();
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const [registros, setRegistros] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [materiais, setMateriais] = useState([]);
  const [obras, setObras] = useState([]);
  const [form, setForm] = useState(inicial);
  const [editando, setEditando] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState("");

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setCarregandoLista(true);
    try {
    const [snapRegistros, snapMateriais, snapObras] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, COLECAO_CARACTERIZACAO_ASFALTO), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblListaMateriaisCadastrados"), orderBy("codigo", "desc")))),
      getDocs(consultaTenantAtual(collection(db, "obras"))),
    ]);
    setRegistros(snapRegistros.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMateriais(snapMateriais.docs.map((item) => ({ id: item.id, ...item.data() })));
    setObras(snapObras.docs.map((item) => ({ id: item.id, ...item.data() })));
    } finally {
      setCarregandoLista(false);
    }
  }

  function novo() {
    setEditando(null);
    setForm({
      ...inicial,
      codigo: String(registros.length + 1).padStart(3, "0"),
      determinacoes: tres(),
    });
    setAberto(true);
  }

  function editar(item) {
    setEditando(item);
    setForm({ ...inicial, ...item, determinacoes: [0, 1, 2].map((i) => ({ ...(item.determinacoes?.[i] || {}) })) });
    setAberto(true);
  }

  function selecionarMaterial(material) {
    const obra = obras.find((item) => item.id === material?.ObraId);
    setForm((atual) => ({
      ...atual,
      materialId: material?.id || "",
      material: material?.Descricao || "",
      codigoMaterial: material?.codigo || "",
      cliente: material?.Cliente || atual.cliente || "",
      clienteId: material?.ClienteId || obra?.clienteId || atual.clienteId || "",
      obraId: material?.ObraId || atual.obraId || "",
      obra: obra?.nome || atual.obra || "",
      procedencia: material?.Fornecedor || atual.procedencia || "",
      tipoAgregado: tipoAgregadoDoMaterial(material),
    }));
  }

  function selecionarCliente(cliente) {
    setForm((atual) => ({
      ...atual,
      cliente: cliente || "",
      clienteId: materiais.find((item) => textoBusca(item.Cliente) === textoBusca(cliente))?.ClienteId || "",
      obra: "",
      obraId: "",
      material: "",
      materialId: "",
      codigoMaterial: "",
    }));
  }

  function selecionarObra(nomeObra) {
    const obraSelecionada = obras.find((obra) => obra.nome === nomeObra);
    setForm((atual) => ({
      ...atual,
      obra: nomeObra || "",
      obraId: obraSelecionada?.id || "",
      material: "",
      materialId: "",
      codigoMaterial: "",
    }));
  }

  const clientesCadastrados = useMemo(() =>
    Array.from(new Set(materiais.map((item) => item.Cliente).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b), "pt-BR")), [materiais]);

  const obrasFiltradas = useMemo(() => {
    const cliente = textoBusca(form.cliente);
    const obrasIds = cliente
      ? new Set(materiais
          .filter((material) => textoBusca(material.Cliente) === cliente)
          .map((material) => material.ObraId)
          .filter(Boolean))
      : null;
    return obras
      .filter((obra) => !obrasIds || obrasIds.has(obra.id))
      .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
  }, [form.cliente, materiais, obras]);

  const materiaisFiltrados = useMemo(() => {
    const cliente = textoBusca(form.cliente);
    const obra = textoBusca(form.obra);
    return materiais.filter((material) => {
      if (!tipoAgregadoDoMaterial(material)) return false;
      const obraMaterial = obras.find((item) => item.id === material.ObraId);
      const clienteConfere = !cliente || textoBusca(material.Cliente) === cliente;
      const obraConfere = form.obraId
        ? material.ObraId === form.obraId
        : !obra || textoBusca(obraMaterial?.nome) === obra;
      return clienteConfere && obraConfere;
    });
  }, [form.cliente, form.obra, form.obraId, materiais, obras]);

  async function salvar() {
    if (!form.materialId) return alert("Selecione um material cadastrado.");
    if (!form.tipoAgregado) return alert("O material selecionado precisa estar cadastrado como agregado graúdo ou agregado miúdo.");
    const dadosIniciais = {
      codigo: form.codigo,
      data: form.data,
      tipoAgregado: form.tipoAgregado,
      materialId: form.materialId,
      material: form.material,
      codigoMaterial: form.codigoMaterial,
      cliente: form.cliente,
      clienteId: form.clienteId,
      obraId: form.obraId,
      obra: form.obra,
      procedencia: form.procedencia,
      camadaEstudo: form.camadaEstudo,
      atualizadoEm: new Date(),
    };
    if (editando?.id) {
      await updateDoc(doc(db, COLECAO_CARACTERIZACAO_ASFALTO, editando.id), dadosIniciais);
    } else {
      const referencia = await addDoc(
        collection(db, COLECAO_CARACTERIZACAO_ASFALTO),
        aplicarTenantAtual({
          ...dadosIniciais,
          determinacoes: tres(),
          resultados: {},
          temperaturaAgua: "25",
          faixaDnitAsfalto: "C125",
          dataGranulometria: form.data,
          operadorGranulometria: "",
          massaInicialGranulometriaA: "",
          massaInicialGranulometriaB: "",
          granulometriaAsfalto: criarGranulometriaAsfalto(),
          observacoes: "",
          criadoEm: new Date(),
        })
      );
      setAberto(false);
      history.push(`/app/caracterizacao-asfalto/${referencia.id}`);
      return;
    }
    setAberto(false);
    await carregar();
  }

  const filtrados = registros.filter((item) =>
    contemTextoPesquisa([item.codigo, item.material, item.codigoMaterial, item.cliente, item.obra, item.tipoAgregado]
      .join(" ").toLowerCase(), filtro.toLowerCase()));

  return <>
    <Navbar />
    <Box sx={{ p: { xs: 2, md: 3 }, pt: 12 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} mb={2}>
        <Box><Typography variant="h5" fontWeight={900}>Caracterização de Agregado para Asfalto</Typography><Typography color="text.secondary">Massas específicas, absorção e densidade efetiva</Typography></Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={novo}>Nova caracterização</Button>
      </Stack>
      <TextField fullWidth size="small" label="Pesquisar" value={filtro} onChange={(e) => setFiltro(e.target.value)} sx={{ mb: 2 }} />
      <Paper sx={{ overflowX: "auto" }}><Table size="small"><TableHead><TableRow>
        <TableCell>Código</TableCell><TableCell>Material</TableCell><TableCell>Tipo</TableCell><TableCell>Obra</TableCell><TableCell>Densidade efetiva</TableCell><TableCell>Ações</TableCell>
      </TableRow></TableHead><TableBody>
      {carregandoLista && <TableRow><TableCell colSpan={6}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
      {!carregandoLista && filtrados.map((item) => <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => history.push(`/app/caracterizacao-asfalto/${item.id}`)}>
        <TableCell>{item.codigo}</TableCell><TableCell>{item.material}</TableCell><TableCell>{item.tipoAgregado === "graudo" ? "Graúdo" : "Miúdo"}</TableCell><TableCell>{item.obra || "-"}</TableCell>
        <TableCell>{formatar(item.resultados?.massaEspecificaEfetiva)}</TableCell><TableCell>
          <Tooltip title="Editar cadastro inicial">
            <IconButton size="small" onClick={(event) => { event.stopPropagation(); editar(item); }}>
              <EditIcon sx={{ color: "#0288d1" }} />
            </IconButton>
          </Tooltip>
          <HistoricoCadastro colecao={COLECAO_CARACTERIZACAO_ASFALTO} documentoId={item.id} registro={item} />
        </TableCell>
      </TableRow>)}
      {!carregandoLista && filtrados.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nenhuma caracterização cadastrada.</TableCell></TableRow>}
      </TableBody></Table></Paper>
    </Box>

    <Dialog open={aberto} onClose={() => !salvandoCadastro && setAberto(false)} fullWidth maxWidth="lg">
      <DialogTitle>
        {editando ? "Editar cadastro inicial" : "Nova caracterização"}
        {form.tipoAgregado ? ` — agregado ${form.tipoAgregado === "graudo" ? "graúdo" : "miúdo"}` : ""}
      </DialogTitle>
      <DialogContent dividers><Stack spacing={2}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          <TextField label="Código" size="small" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
          <TextField label="Data" type="date" size="small" InputLabelProps={{ shrink: true }} value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
          <TextField
            label="Tipo do agregado"
            size="small"
            value={form.tipoAgregado === "graudo" ? "Graúdo" : form.tipoAgregado === "miudo" ? "Miúdo" : "Definido após selecionar o material"}
            InputProps={{ readOnly: true }}
          />
          <TextField label="Camada/Estudo" size="small" value={form.camadaEstudo} onChange={(e) => setForm({ ...form, camadaEstudo: e.target.value })} />
          <Autocomplete freeSolo options={clientesCadastrados} value={form.cliente || ""} sx={{ gridColumn: { md: "1 / -1" } }} onChange={(_, valor) => selecionarCliente(valor || "")} onInputChange={(_, valor) => { if (valor !== form.cliente) selecionarCliente(valor || ""); }} renderInput={(params) => <TextField {...params} label="Cliente" size="small" />} />
          <Autocomplete freeSolo options={obrasFiltradas.map((obra) => obra.nome).filter(Boolean)} value={form.obra || ""} sx={{ gridColumn: { md: "1 / -1" } }} onChange={(_, valor) => selecionarObra(valor || "")} onInputChange={(_, valor) => { if (valor !== form.obra) selecionarObra(valor || ""); }} renderInput={(params) => <TextField {...params} label="Obra" size="small" />} />
          <Autocomplete options={materiaisFiltrados} sx={{ gridColumn: { md: "1 / -1" } }} getOptionLabel={(item) => `${item.codigo || ""} - ${item.Descricao || ""} - ${item.Fornecedor || ""}`} isOptionEqualToValue={(a, b) => a.id === b.id} value={materiais.find((item) => item.id === form.materialId) || null} onChange={(_, valor) => selecionarMaterial(valor)} renderInput={(params) => <TextField {...params} label="Material cadastrado" size="small" />} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Após salvar este cadastro inicial, os dados do ensaio serão preenchidos na tela de detalhes.
        </Typography>
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setAberto(false)}>Cancelar</Button><Button variant="contained" disabled={salvandoCadastro} onClick={() => executarSalvamento(salvar)}>{salvandoCadastro ? "Salvando..." : "Salvar"}</Button></DialogActions>
    </Dialog>
  </>;
}
